"use client";

import { Box } from "@mui/material";
import { LinkBubbleMenu, RichTextEditor, TableBubbleMenu } from "mui-tiptap";
import useExtensions from "./useExtensions";

export interface EditorProps extends React.PropsWithChildren<any> {
  content: string;
}

const contentStyles = {
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    marginTop: 2,
    marginBottom: 1,
  },
  "& p": {
    marginBottom: 1,
  },
  "& ul, & ol": {
    marginLeft: 3,
    marginBottom: 1,
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    "&.resizable-image": {
      cursor: "move",
      "&:hover": {
        outline: "2px solid #1976d2",
      },
    },
  },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 1,
    "& th, & td": {
      border: "1px solid",
      borderColor: "divider",
      padding: 1,
    },
  },
  "& blockquote": {
    borderLeft: "4px solid",
    borderColor: "divider",
    paddingLeft: 2,
    marginLeft: 0,
    marginRight: 0,
    fontStyle: "italic",
  },
  "& pre": {
    backgroundColor: "grey.100",
    padding: 2,
    borderRadius: 1,
    overflow: "auto",
  },
  "& code": {
    backgroundColor: "grey.100",
    padding: "0.2em 0.4em",
    borderRadius: 1,
    fontSize: "0.9em",
  },
  "& .recipe-tags": {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
    marginBottom: 2,
    "& .tag.badge": {
      display: "inline-flex",
      alignItems: "center",
      padding: "0.375rem 0.75rem",
      borderRadius: "1rem",
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.5,
      transition: "all 0.2s ease",
      "&.cuisine-type": {
        backgroundColor: "rgba(25, 118, 210, 0.1)",
        color: "rgb(25, 118, 210)",
        border: "1px solid rgba(25, 118, 210, 0.3)",
      },
      "&.meal-type": {
        backgroundColor: "rgba(156, 39, 176, 0.1)",
        color: "rgb(156, 39, 176)",
        border: "1px solid rgba(156, 39, 176, 0.3)",
      },
      "&.difficulty": {
        backgroundColor: "rgba(255, 152, 0, 0.1)",
        color: "rgb(255, 152, 0)",
        border: "1px solid rgba(255, 152, 0, 0.3)",
      },
      "&.cooking-method": {
        backgroundColor: "rgba(76, 175, 80, 0.1)",
        color: "rgb(76, 175, 80)",
        border: "1px solid rgba(76, 175, 80, 0.3)",
      },
      "&.meal-event": {
        backgroundColor: "rgba(158, 158, 158, 0.1)",
        color: "rgb(97, 97, 97)",
        border: "1px solid rgba(158, 158, 158, 0.3)",
      },
    },
  },
  "& .recipe-meta": {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    marginBottom: 2,
    padding: 1.5,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    borderRadius: 1,
    "& > span": {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      fontSize: "0.875rem",
      color: "text.secondary",
      fontWeight: 500,
    },
    "& span[data-field]": {
      "&::before": {
        content: 'attr(data-field) ": "',
        fontWeight: 600,
        color: "text.primary",
        marginRight: 0,
      },
    },
    "& span.prep-time, & span.cook-time": {
      "&::after": {
        content: '" phút"',
      },
    },
    "& span.servings": {
      "&::after": {
        content: '" người"',
      },
    },
    "& span[data-field='prep-time'], & span[data-field='cook-time']": {
      "&::after": {
        content: '" phút"',
      },
    },
    "& span[data-field='servings']": {
      "&::after": {
        content: '" người"',
      },
    },
  },
};

const Editor: React.FC<EditorProps> = ({ content }) => {
  const extensions = useExtensions({
    placeholder: "Add your own content here...",
  });

  return (
    <Box
      sx={{
        width: "70%",
        margin: "10px auto 0",
        "& .ProseMirror": {
          height: "100%",
          overflowY: "auto",
          "&:focus": {
            outline: "none",
          },
          ...contentStyles,
          '& div[data-type="iframe"]': {
            position: "relative",
            width: "100%",
            paddingTop: "56.25%",
            marginBottom: 2,
            "& iframe": {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            },
          },
        },
        "& .MuiRichTextEditor-RichTextField": {
          height: "calc(100vh - 200px)",
          overflow: "hidden",
        },
      }}
    >
      <RichTextEditor
        immediatelyRender={false}
        extensions={extensions}
        content={content}
        editable={false}
        editorProps={{
          attributes: {
            class: "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
          },
        }}
        RichTextFieldProps={{
          variant: "standard",
          MenuBarProps: {
            hide: true,
          },
        }}
      >
        {() => (
          <>
            <LinkBubbleMenu />
            <TableBubbleMenu />
          </>
        )}
      </RichTextEditor>
    </Box>
  );
};

export default Editor;
